import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  Snackbar,
} from "@mui/material";

import Titulo from "../components/titulos";
import { importarPrecios } from "../service/apiPrecioResiduoAlmacen";

const ImportarPrecios = ({ datos }) => {
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState("TODOS");
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [procesando, setProcesando] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const inputArchivoRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(datos) && datos.length > 0) {
      setItems(datos);
    }
  }, [datos]);

  const handleSeleccionarArchivo = (event) => {
    const archivoSeleccionado =
      event.target.files?.[0];

    if (!archivoSeleccionado) {
      return;
    }

    setArchivo(archivoSeleccionado);

    const url = URL.createObjectURL(
      archivoSeleccionado
    );

    setVistaPrevia(url);
  };

  const handleProcesarImagen = async () => {
    if (!archivo) {
      setSnackbarMessage(
        "Debe seleccionar una imagen."
      );
      setShowSnackbar(true);
      return;
    }

    try {
      setProcesando(true);

      const formData = new FormData();

      formData.append(
        "archivo",
        archivo
      );

      const response =
        await importarPrecios(formData);

      const nuevosItems =
        response?.resultado?.items || [];

      if (nuevosItems.length === 0) {
        setSnackbarMessage(
          "No se encontraron datos para procesar en la imagen."
        );
        setShowSnackbar(true);
        return;
      }

      setItems(nuevosItems);
      setFiltro("TODOS");
      setVistaPrevia(null);

      setSnackbarMessage(
        "Imagen procesada correctamente."
      );
      setShowSnackbar(true);
    } catch (error) {
      console.error(
        "Error procesando imagen de precios:",
        error
      );

      setSnackbarMessage(
        "Hubo un error al procesar la imagen."
      );
      setShowSnackbar(true);
    } finally {
      setProcesando(false);
    }
  };

  const handleSeleccionar = (index) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              seleccionado:
                !item.seleccionado,
            }
          : item
      )
    );
  };

  const handleSeleccionarTodos = () => {
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        seleccionado:
          item.match === "EXACT",
      }))
    );
  };

  const handleDeseleccionarTodos = () => {
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        seleccionado: false,
      }))
    );
  };

  const itemsFiltrados = items.filter(
    (item) => {
      if (filtro === "EXACT") {
        return item.match === "EXACT";
      }

      if (filtro === "NOT_FOUND") {
        return item.match === "NOT_FOUND";
      }

      if (filtro === "INVALID") {
        return item.match === "INVALID";
      }

      return true;
    }
  );

  const total = items.length;

  const exactos = items.filter(
    (item) => item.match === "EXACT"
  ).length;

  const noEncontrados = items.filter(
    (item) =>
      item.match === "NOT_FOUND"
  ).length;

  const invalidos = items.filter(
    (item) => item.match === "INVALID"
  ).length;

  const seleccionados = items.filter(
    (item) =>
      item.seleccionado === true
  ).length;

  const formatearPrecio = (valor) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === ""
    ) {
      return "-";
    }

    return Number(valor).toLocaleString(
      "es-ES",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }
    );
  };

  const obtenerEstado = (match) => {
    if (match === "EXACT") {
      return (
        <Typography
          sx={{
            color: "#2e7d32",
            fontWeight: 600,
          }}
        >
          Coincide
        </Typography>
      );
    }

    if (match === "NOT_FOUND") {
      return (
        <Typography
          sx={{
            color: "#e65100",
            fontWeight: 600,
          }}
        >
          No encontrado
        </Typography>
      );
    }

    return (
      <Typography
        sx={{
          color: "#c62828",
          fontWeight: 600,
        }}
      >
        Inválido
      </Typography>
    );
  };

  return (
    <>
      <Titulo type="precio" name="Importar Precios"
      />

      <Box className="boxContainer" 
        sx={{
          display: "block",
          width: "100%",
        }}
      >

        {/* Selección de imagen */}
        <Box
          sx={{
            width: "80%",
            margin: "20px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              width: "100%",
              padding: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <input
                ref={inputArchivoRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={
                  handleSeleccionarArchivo
                }
              />

              <Button
                variant="outlined"
                size="small"
                disabled={procesando}
                onClick={() =>
                  inputArchivoRef.current?.click()
                }
              >
                Seleccionar imagen
              </Button>

              <Button
                variant="contained"
                size="small"
                disabled={
                  !archivo || procesando
                }
                onClick={
                  handleProcesarImagen
                }
                sx={{
                  backgroundColor:
                    "#2e7d32",
                }}
              >
                {procesando ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                    Procesando...
                  </>
                ) : (
                  "Procesar imagen"
                )}
              </Button>

              {archivo && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  Archivo seleccionado:{" "}
                  {archivo.name}
                </Typography>
              )}

              {vistaPrevia && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={vistaPrevia}
                    alt="Vista previa de precios"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 400,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Resumen */}
        {items.length > 0 && (
          <Box
            sx={{
              width: "90%",
              margin: "20px auto",
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Paper
              sx={{
                padding: 2,
                minWidth: 180,
                flex: "1 1 180px",
                maxWidth: 240,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Total
              </Typography>

              <Typography
                variant="h6"
                fontWeight={700}
              >
                {total}
              </Typography>
            </Paper>

            <Paper
              sx={{
                padding: 2,
                minWidth: 180,
                flex: "1 1 180px",
                maxWidth: 240,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Coincidencias
              </Typography>

              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: "#2e7d32",
                }}
              >
                {exactos}
              </Typography>
            </Paper>

            <Paper
              sx={{
                padding: 2,
                minWidth: 180,
                flex: "1 1 180px",
                maxWidth: 240,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                No encontrados
              </Typography>

              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: "#e65100",
                }}
              >
                {noEncontrados}
              </Typography>
            </Paper>

            <Paper
              sx={{
                padding: 2,
                minWidth: 180,
                flex: "1 1 180px",
                maxWidth: 240,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Seleccionados
              </Typography>

              <Typography
                variant="h6"
                fontWeight={700}
              >
                {seleccionados}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Filtros */}
        {items.length > 0 && (
          <Box
            sx={{
              width: "90%",
              margin: "0 auto 15px",
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: 200 }}
            >
              <InputLabel>
                Mostrar
              </InputLabel>

              <Select
                value={filtro}
                label="Mostrar"
                onChange={(event) =>
                  setFiltro(
                    event.target.value
                  )
                }
              >
                <MenuItem value="TODOS">
                  Todos
                </MenuItem>

                <MenuItem value="EXACT">
                  Coincidencias
                </MenuItem>

                <MenuItem value="NOT_FOUND">
                  No encontrados
                </MenuItem>

                <MenuItem value="INVALID">
                  Inválidos
                </MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={
                  handleSeleccionarTodos
                }
              >
                Seleccionar coincidencias
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={
                  handleDeseleccionarTodos
                }
              >
                Deseleccionar todos
              </Button>
            </Box>
          </Box>
        )}

        {/* Tabla */}
        {items.length > 0 && (
          <Box
            sx={{
              width: "90%",
              margin: "0 auto",
            }}
          >
            <TableContainer
              component={Paper}
              className="tableContainer"
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Seleccionar
                    </TableCell>

                    <TableCell>
                      Residuo detectado
                    </TableCell>

                    <TableCell>
                      Residuo BD
                    </TableCell>

                    <TableCell>
                      LER
                    </TableCell>

                    <TableCell>
                      Código interno
                    </TableCell>

                    <TableCell align="right">
                      Precio fuente
                    </TableCell>

                    <TableCell align="right">
                      Precio KG
                    </TableCell>

                    <TableCell align="right">
                      Maestro actual
                    </TableCell>

                    <TableCell align="right">
                      Maestro nuevo
                    </TableCell>

                    <TableCell>
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {itemsFiltrados.map(
                    (item, index) => {
                      const indiceReal =
                        items.indexOf(item);

                      return (
                        <TableRow
                          key={`${
                            item.res_id ||
                            "sin-id"
                          }-${index}`}
                          className="tableCeldas"
                        >
                          <TableCell>
                            <Checkbox
                              size="small"
                              checked={
                                item.seleccionado ===
                                true
                              }
                              disabled={
                                item.match !==
                                "EXACT"
                              }
                              onChange={() =>
                                handleSeleccionar(
                                  indiceReal
                                )
                              }
                            />
                          </TableCell>

                          <TableCell>
                            {item.nombre_fuente ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {item.nombre_bd ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {item.res_ler ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {item.res_cod_interno ||
                              "-"}
                          </TableCell>

                          <TableCell align="right">
                            {formatearPrecio(
                              item.precio_fuente
                            )}{" "}
                            {item.unidad_fuente ||
                              ""}
                          </TableCell>

                          <TableCell align="right">
                            {formatearPrecio(
                              item.precio_kg
                            )}
                          </TableCell>

                          <TableCell align="right">
                            {formatearPrecio(
                              item.precio_maestro_actual
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                            }}
                          >
                            {formatearPrecio(
                              item.precio_maestro_nuevo
                            )}
                          </TableCell>

                          <TableCell>
                            {obtenerEstado(
                              item.match
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}

                  {itemsFiltrados.length ===
                    0 && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                      >
                        <Typography
                          sx={{
                            padding: 4,
                            color:
                              "text.secondary",
                          }}
                        >
                          No hay datos para
                          mostrar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Aplicar cambios - pendiente */}
        {items.length > 0 && (
          <Box
            sx={{
              width: "90%",
              margin: "15px auto",
              display: "flex",
              justifyContent:
                "flex-end",
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled
              sx={{
                backgroundColor:
                  "#1abd9c",
              }}
            >
              Aplicar cambios
            </Button>
          </Box>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() =>
          setShowSnackbar(false)
        }
        message={snackbarMessage}
      />
    </>
  );
};

export default ImportarPrecios;